import React, { useState } from 'react';
import ReactGA from 'react-ga4';
import { EverifyUserRole, MyI9TrainingStatus, User } from '@vis-auth/vis-user-client';
import { useLogout } from '@vis-auth/react';
import { EveHeader } from '@ver-uds/e-verify';
import { useAccountQuery } from '../services';
import { useEnvironment } from '../context/Environment/EnvironmentContext';
import { useUser } from '../context/User/UserContext';
import { useGlobalData } from '../context/Global/GlobalContext';
import EnrollmentExitModal from './Enrollment/EnrollmentExitModal';
import { getAuthServiceMFA } from '../utils/util';

const Header = (): JSX.Element => {
  const {
    roles,
    accountId,
    firstName,
    lastName,
    email,
    myI9TrainingStatus,
    tutorialComplete,
    mfaEnabled,
  } = useUser() as User;
  const { data: accountData } = useAccountQuery(accountId?.toString(), { useErrorBoundary: false });

  const accountType = accountData?.type;
  const webServiceAccess = accountData?.webServiceAccess?.enabled || false;

  const evePlusTrainingComplete =
    myI9TrainingStatus === MyI9TrainingStatus.COMPLETE || myI9TrainingStatus === MyI9TrainingStatus.OVERRIDDEN;
  const evePlusEnabled = accountData?.myI9?.enabled ?? false;

  // Determines if the eve+ training link is shown
  const showTrainingLink: boolean = evePlusTrainingComplete && evePlusEnabled;

  // Hide nav items if tutorial is incomplete or if eve+ training has not been completed
  const hideNavItems = !tutorialComplete || (evePlusEnabled && !evePlusTrainingComplete);

  const {
    SELF_TEST_LINK_ENABLED,
    GTAG_ID,
    AUTH_URL,
    LOGIN_DOT_GOV_AUTH_CLIENTID,
    LOGIN_DOT_GOV_URL,
  } = useEnvironment();
  const { isOnEnrollment } = useGlobalData();

  const logout = useLogout();

  const [exitModalIsOpen, setExitModalIsOpen] = useState(false);

  const logoutFn = () => {
    if (isOnEnrollment) {
      setExitModalIsOpen(true);
    } else if (mfaEnabled) {
      const authService = getAuthServiceMFA(AUTH_URL, LOGIN_DOT_GOV_AUTH_CLIENTID, LOGIN_DOT_GOV_URL);
      authService.mfaLogoutRedirect();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      logout();
    }
  };

  if (!ReactGA.isInitialized && GTAG_ID !== undefined) {
    ReactGA.initialize(GTAG_ID, {
      // eslint-disable-next-line
      gtagOptions: { send_page_view: false },
    });
  }

  return (
    <>
      {roles.includes(EverifyUserRole.ENROLLMENT) ? (
        <EveHeader
          showSelfTestLink={SELF_TEST_LINK_ENABLED || false}
          onLogOut={() => () => {
            logoutFn();
          }}
          hideNavItems
          userMenuLabel={email}
        />
      ) : (
        <>
          {firstName && accountType && (
            <EveHeader
              user={{ firstName, lastName, roles: roles as EverifyUserRole[], mfaEnabled }}
              account={{ accountType, webServiceAccess }}
              showSelfTestLink={SELF_TEST_LINK_ENABLED || false}
              onLogOut={() => () => {
                logoutFn();
              }}
              showTrainingLink={showTrainingLink}
              hideNavItems={hideNavItems}
            />
          )}
        </>
      )}
      <EnrollmentExitModal when={exitModalIsOpen} onOK={logout} onCancel={() => setExitModalIsOpen(false)} />
    </>
  );
};

export default Header;
